import { __decorate } from "tslib";
import React from 'react';
import PropTypes from 'prop-types';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';
import isPromise from 'is-promise';
import { T } from 'choerodon-ui/lib/upload/utils';
import Button from '../button/Button';
import { ButtonColor } from '../button/enum';
import autobind from '../_util/autobind';
import { FormField } from '../field/FormField';
import Icon from '../icon';
import message from '../message';
import Modal from '../modal';
import UploadList from './UploadList';
import Tooltip from '../tooltip/Tooltip';
import { $l } from '../locale-context';
import isIE from '../_util/isIE';
/**
 * 把XMLHttpRequest对象的返回信息转化为字符串
 *
 * @param {XMLHttpRequest} xhr
 * @returns {string}
 * @memberof Upload
 */
function getResponse(xhr) {
    const res = xhr.responseText || xhr.response;
    if (!res) {
        return res;
    }
    try {
        return JSON.parse(res).message;
    }
    catch (e) {
        return '';
    }
}
let Upload = class Upload extends FormField {
    constructor(props, context) {
        super(props, context);
        this.saveNativeInputElement = elem => (this.nativeInputElement = elem);
        /**
         * 传递包装按钮的点击事件
         *
         */
        this.handleWrapperBtnClick = () => {
            this.nativeInputElement.click();
        };
        this.handleUploadBtnClick = () => {
            this.startUpload();
        };
        /**
         * 文件上传前的回调
         *
         * @param {UploadFile}
         * @param {UploadFile[]}
         * @memberof Upload
         */
        this.beforeUpload = (file, fileList) => {
            const { beforeUpload } = this.props;
            if (!beforeUpload) {
                return true;
            }
            const result = beforeUpload(file, fileList);
            if (result === false) {
                this.removeFileItem(file);
                return false;
            }
            if (isPromise(result)) {
                return result;
            }
            return true;
        };
        this.startUpload = () => {
            const fileList = [...this.fileList];
            if (fileList.length) {
                // <-- 当有文件时才上传
                this.uploadFiles(fileList);
                // this.nativeInputElement.value = '';
            }
            else {
                Modal.error($l('Upload', 'no_file'));
            }
        };
        this.handleRemove = (file) => {
            // this.removeFileItem(file);
            // this.upload.abort(file);
            file.status = 'removed';
            this.handleOnRemove(file);
        };
        runInAction(() => {
            this.fileList = props.uploadFileList || props.defaultFileList || [];
        });
    }
    componentWillReceiveProps(nextProps) {
        const { uploadFileList } = nextProps;
        if (uploadFileList !== this.fileList && uploadFileList !== undefined) {
            this.fileList = uniqBy(uploadFileList, (item) => item.uid);
        }
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'accept',
            'action',
            'data',
            'header',
            'multiple',
            'uploadImmediately',
            'fileListMaxLength',
            'showPreviewImage',
            'previewImageWidth',
            'showUploadBtn',
            'showUploadList',
            'onRemoveFile',
            'onUploadSuccess',
            'onUploadError',
            'onFileChange',
            'beforeUpload',
            'withCredentials',
            'partialUpload',
            'appendUpload',
            'uploadFileList',
        ]);
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        delete otherProps.ref;
        delete otherProps.onChange;
        return otherProps;
    }
    render() {
        const { prefixCls, props: { action: formAction, children, multiple, accept, name = 'file', // <-- convince ts
        uploadImmediately, showPreviewImage, previewImageWidth, showUploadBtn, showUploadList, extra, }, } = this;
        const uploadProps = {
            multiple,
            accept: accept ? accept.join(',') : undefined,
            action: formAction,
            name,
            type: 'file',
            ref: this.saveNativeInputElement,
            onChange: this.handleChange,
            ...this.getOtherProps(),
        };
        const inputWrapperBtn = [
            React.createElement(Button, { key: "upload-btn", onClick: this.handleWrapperBtnClick },
                React.createElement(Icon, { type: "insert_drive_file" }),
                React.createElement("span", null, children || $l('Upload', 'file_selection'))),
            React.createElement("input", Object.assign({ key: "upload" }, uploadProps, { hidden: true })),
        ];
        const uploadBtn = (React.createElement(Tooltip, { title: $l('Upload', 'click_to_upload'), placement: "right" },
            React.createElement(Button, { color: ButtonColor.primary, onClick: this.handleUploadBtnClick },
                React.createElement(Icon, { type: "file_upload" }))));
        /**
         * to solve the ie11 dispaly inline-block with the button cause unaligned
         */
        const IeStyle = isIE() ? { display: '-ms-inline-flexbox' } : {};
        return (React.createElement("div", { className: `${prefixCls}`, style: IeStyle },
            React.createElement("div", { className: "flex-wrapper" },
                React.createElement("div", { className: `${prefixCls}-select` },
                    inputWrapperBtn,
                    !uploadImmediately && showUploadBtn ? uploadBtn : null),
                React.createElement("div", null, extra)),
            showUploadList ? (React.createElement(UploadList, { previewImageWidth: previewImageWidth, showPreviewImage: showPreviewImage, items: [...this.fileList], remove: this.handleRemove })) : null));
    }
    /**
     * 处理<input type="file">元素的change事件，
     * 主要是取出事件对象中的files对象并传递给uploadFiles方法
     *
     * @param {*} e <input>元素的change事件对象
     * @memberof Upload
     */
    handleChange(e) {
        if (e.target.value === '') {
            return;
        }
        const { appendUpload, defaultFileList, uploadFileList } = this.props;
        const fileList = e.target.files;
        const files = Array.from(fileList).slice(0);
        const tempFileList = appendUpload || defaultFileList || uploadFileList ? this.fileList.slice() : [];
        const fileBuffer = [];
        files.forEach((file, index) => {
            file.uid = this.getUid(index);
            file.url = URL.createObjectURL(file);
            const res = this.beforeUpload(file, files);
            if (!res) {
                return;
            }
            fileBuffer.push(file);
        });
        this.fileList = [...tempFileList, ...fileBuffer];
        const { uploadImmediately, onFileChange } = this.props;
        e.target.value = '';
        if (uploadImmediately) {
            if (!isEmpty(fileBuffer)) {
                this.uploadFiles(this.fileList);
            }
        }
        if (onFileChange) {
            onFileChange(this.fileList.slice());
        }
    }
    /**
     * 分别上传fileList中的每个文件对象
     *
     * @param {UploadFile[]} fileList 文件对象数组
     * @returns {void}
     * @memberof Upload
     */
    uploadFiles(fileList) {
        const { action: formAction, accept, fileListMaxLength = 0, // <-- convince ts
        partialUpload, } = this.props;
        if (!formAction) {
            Modal.error($l('Upload', 'upload_path_unset'));
            return;
        }
        if (!this.isAcceptFiles(fileList)) {
            Modal.error($l('Upload', 'not_acceptable_prompt') + accept.join(','));
            return;
        }
        if (fileListMaxLength !== 0 && this.fileList.length > fileListMaxLength) {
            Modal.error(`${$l('Upload', 'file_list_max_length')}: ${fileListMaxLength}`);
            return;
        }
        const files = partialUpload
            ? Array.from(fileList)
                .slice(0)
                .filter(item => !item.status || item.status !== 'success')
            : Array.from(fileList).slice(0);
        const that = this;
        if (!files.length) {
            message.info($l('Upload', 'been_uploaded'));
        }
        files.forEach((file, index) => {
            file.uid = this.getUid(index);
            setTimeout(() => {
                that.upload(file);
            }, 0);
        });
    }
    /**
     * 上传每个文件对象
     *
     * @param {*} file
     * @returns {void}
     * @memberof Upload
     */
    /* istanbul ignore next */
    upload(file) {
        const { data, action: formAction, headers, name: filename, withCredentials: xhrWithCredentials, } = this.props;
        if (typeof XMLHttpRequest === 'undefined') {
            return;
        }
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        // 修改文件状态，方便UploadList判断是否展示进度条
        file.status = 'uploading';
        if (xhr.upload) {
            xhr.upload.onprogress = e => {
                let percent = 0;
                if (e.total > 0) {
                    percent = (e.loaded / e.total) * 100;
                }
                this.handleProgress(percent, file);
            };
        }
        if (data) {
            const newData = typeof data === 'function' ? data(file) : data;
            Object.keys(newData).forEach(key => formData.append(key, newData[key]));
        }
        // TODO: `filename` default value needs better implementation
        formData.append(filename || 'file', file);
        const errorMsg = `cannot post ${formAction} ${xhr.status}`;
        if (xhrWithCredentials && 'withCredentials' in xhr) {
            xhr.withCredentials = true;
        }
        xhr.open('post', formAction, true);
        xhr.onload = () => {
            // 以二开头的状态码都认为是成功，暂定？
            const isSuccessful = xhr.status.toString().startsWith('2');
            if (isSuccessful) {
                this.handleSuccess(xhr.status, xhr.response, file);
            }
            else {
                this.handleError(new Error(errorMsg), getResponse(xhr), xhr.response, file);
            }
        };
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        if (headers !== undefined) {
            Object.keys(headers).forEach(key => {
                if ({}.hasOwnProperty.call(headers, key)) {
                    xhr.setRequestHeader(key, headers[key]);
                }
            });
        }
        xhr.send(formData);
        xhr.onerror = () => {
            this.handleError(new Error(errorMsg), getResponse(xhr), xhr.response, file);
        };
        xhr.ontimeout = () => {
            const timeoutMsg = `The request post for ${action} timed out`;
            this.handleError(new Error(timeoutMsg), getResponse(xhr), xhr.response, file);
        };
    }
    /**
     * 处理上传成功的函数，根据结果设置文件对象的状态，
     * 用于在UploadList中的展示
     *
     * @param {number} status HTTP状态码
     * @param {string} response 响应对象
     * @param {UploadFile} file 文件对象
     * @returns
     */
    handleSuccess(status, response, file) {
        const targetItem = this.getFileItem(file);
        if (targetItem) {
            const { onUploadSuccess } = this.props;
            targetItem.status = status === 200 ? 'success' : 'done';
            targetItem.response = response;
            if (onUploadSuccess) {
                onUploadSuccess(response, file);
            }
            this.forceUpdate();
        }
    }
    /**
     * 处理上传进度变化的函数，更新文件对象中的percent值，
     * 用于在UploadList中展示
     *
     * @param {number} percent 上传百分比
     * @param {UploadFile} file 文件对象
     * @returns
     */
    handleProgress(percent, file) {
        const { onUploadProgress } = this.props;
        const targetItem = this.getFileItem(file);
        if (targetItem) {
            targetItem.percent = percent;
            if (onUploadProgress) {
                onUploadProgress(percent, file);
            }
            this.forceUpdate();
        }
    }
    /**
     * 处理上传出错的函数，用于设置文件对象的status值，
     *
     * @param {Error} error 错误对象
     * @param {string} responseText 处理成字符串的响应对象
     * @param {UploadFile} file 文件对象
     * @returns
     */
    handleError(error, responseText, response, file) {
        const { onUploadError } = this.props;
        const targetItem = this.getFileItem(file);
        if (targetItem) {
            targetItem.status = 'error';
            targetItem.error = error;
            targetItem.response = responseText;
            if (onUploadError) {
                onUploadError(error, response, file);
            }
            this.forceUpdate();
        }
    }
    handleOnRemove(file) {
        const { onRemoveFile } = this.props;
        Promise.resolve(typeof onRemoveFile === 'function' ? onRemoveFile(file) : onRemoveFile).then(ret => {
            if (ret === false) {
                return;
            }
            this.removeFileItem(file);
        });
    }
    /**
     * 判断文件后缀名是否合格
     * this.props.accept值为falsy时返回true，否则正常判断
     *
     * @param {UploadFile[]} fileList 文件对象数组
     * @returns {boolean}
     * @memberof Upload
     */
    isAcceptFiles(fileList) {
        const { accept } = this.props;
        if (!accept) {
            return true;
        }
        const acceptTypes = accept.map(type => {
            type = type.replace(/\./g, '\\.');
            type = type.replace(/\*/g, '.*');
            return new RegExp(type);
        });
        return fileList.some(({ name, type }) => acceptTypes.some(acceptType => acceptType.test(name) || acceptType.test(type)));
    }
    /**
     * 使用日期获取一个uid
     *
     * @param {number} index 索引
     * @returns {string}
     * @memberof Upload
     */
    getUid(index) {
        const { prefixCls } = this;
        const now = new Date();
        return `${prefixCls}-${now}-${index}`;
    }
    /**
     * 从文件对象数组中获取一个文件对象的引用，
     * 首先尝试通过uid属性匹配文件对象，若失败则尝试name
     *
     * @param {UploadFile} file
     * @param {UploadFile[]} fileList 文件对象数组
     * @returns {UploadFile}
     * @memberof Upload
     */
    getFileItem(file) {
        const matchKey = file.uid !== undefined ? 'uid' : 'name';
        return this.fileList.find(item => item[matchKey] === file[matchKey]);
    }
    /**
     * 从文件对象数组中移除一个文件对象，
     * 首先尝试通过uid属性匹配文件对象，若失败则尝试name
     *
     * @param {UploadFile} file
     * @param {UploadFile[]} fileList
     * @returns {UploadFile[]}
     * @memberof Upload
     */
    removeFileItem(file) {
        const matchKey = file.uid !== undefined ? 'uid' : 'name';
        const index = this.fileList.findIndex(item => item[matchKey] === file[matchKey]);
        const { uploadFileList } = this.props;
        if (uploadFileList && uploadFileList.length) {
            uploadFileList.splice(index, 1);
            this.fileList.splice(index, 1);
        }
        else {
            this.fileList.splice(index, 1);
        }
    }
};
Upload.displayName = 'Upload';
Upload.propTypes = {
    /**
     * 可接受的上传文件类型
     * 可选值: MIME类型字符串组成的数组
     */
    accept: PropTypes.arrayOf(PropTypes.string),
    /**
     * 上传文件路径
     */
    action: PropTypes.string,
    /**
     * 上传所需参数或者返回上传参数的方法
     * @default
     * {}
     */
    data: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    /**
     * 设置上传的请求头部
     * @default
     * {}
     */
    headers: PropTypes.object,
    /**
     * 是否支持多选文件
     * @default
     * false
     */
    multiple: PropTypes.bool,
    uploadImmediately: PropTypes.bool,
    fileListMaxLength: PropTypes.number,
    showPreviewImage: PropTypes.bool,
    previewImageWidth: PropTypes.number,
    extra: PropTypes.any,
    onFileChange: PropTypes.func,
    beforeUpload: PropTypes.func,
    onRemoveFile: PropTypes.func,
    onUploadProgress: PropTypes.func,
    onUploadSuccess: PropTypes.func,
    onUploadError: PropTypes.func,
    showUploadBtn: PropTypes.bool,
    showUploadList: PropTypes.bool,
    uploadFileList: PropTypes.array,
    withCredentials: PropTypes.bool,
    appendUpload: PropTypes.bool,
    partialUpload: PropTypes.bool,
    ...FormField.propTypes,
};
Upload.defaultProps = {
    ...FormField.defaultProps,
    suffixCls: 'upload',
    multiple: false,
    headers: {},
    data: {},
    action: '',
    name: 'file',
    withCredentials: false,
    appendUpload: false,
    partialUpload: true,
    uploadImmediately: true,
    fileListMaxLength: 0,
    showPreviewImage: true,
    previewImageWidth: 100,
    showUploadBtn: true,
    showUploadList: true,
    beforeUpload: T,
    onUploadSuccess: () => message.success($l('Upload', 'upload_success')),
    onUploadError: () => message.error($l('Upload', 'upload_failure')),
};
__decorate([
    observable
], Upload.prototype, "fileList", void 0);
__decorate([
    action
], Upload.prototype, "componentWillReceiveProps", null);
__decorate([
    autobind,
    action
], Upload.prototype, "handleChange", null);
__decorate([
    autobind,
    action
], Upload.prototype, "uploadFiles", null);
__decorate([
    autobind,
    action
], Upload.prototype, "upload", null);
__decorate([
    action
], Upload.prototype, "handleSuccess", null);
__decorate([
    action
], Upload.prototype, "handleProgress", null);
__decorate([
    action
], Upload.prototype, "handleError", null);
__decorate([
    action
], Upload.prototype, "handleRemove", void 0);
__decorate([
    action
], Upload.prototype, "removeFileItem", null);
Upload = __decorate([
    observer
], Upload);
export default Upload;
